// Budget helpers for roommate property matching.
//
// A matched pair rents ONE place and splits the rent between the two of them,
// so a property "fits" when each person's share (rent ÷ 2) sits within their
// budget — not when the full rent does. Comparing the full rent against a
// single person's budget wrongly hides shared homes that are easily affordable
// once split (e.g. an R8 000 two-bed is R4 000 each).
//
// We only cap the UPPER bound: affordability is the real constraint, and a
// cheaper place is never a reason to hide a listing. A small allowance keeps
// borderline listings visible rather than dropping them at an exact boundary.

export const PAIR_SIZE = 2;

// Keeps listings that are marginally over the shared ceiling visible.
const UPPER_ALLOWANCE = 1.1;

export interface BudgetOverlap {
  min: number;
  max: number;
}

/** Each roommate's share of a property's monthly rent. */
export const perPersonShare = (monthlyRent: number): number =>
  monthlyRent / PAIR_SIZE;

/** e.g. "≈ R4,000 each" — the per-person split shown alongside total rent. */
export const formatPerPersonShare = (monthlyRent: number): string =>
  `≈ R${Math.round(perPersonShare(monthlyRent)).toLocaleString()} each`;

/**
 * Whether a property fits a matched pair's shared budget: each person's split
 * of the rent must be within the pair's overlapping affordability ceiling.
 * Returns true when there is no usable budget overlap (nothing to filter on).
 */
export const fitsPairBudget = (
  monthlyRent: number,
  budgetOverlap?: BudgetOverlap | null
): boolean => {
  if (!budgetOverlap || budgetOverlap.max <= 0) return true;
  return perPersonShare(monthlyRent) <= budgetOverlap.max * UPPER_ALLOWANCE;
};
